import React from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  Typography,
  Card,
  InputNumber,
  Space,
  Table,
  message
} from 'antd'
import { usePrintAgent } from '../../../Context/PrintAgentContext'
import { usePrintConfig } from '../../../Context/PrintConfigContext'
import { CUISINE_TYPES } from '../../../MetaData/cuisine.metadata'

const { Title, Text } = Typography
const { Option } = Select
const OVERRIDE_KEY = 'print_agent_ws_override'
const toHttpBase = (wsUrl: string): string => {
  try {
    const url = new URL(wsUrl)
    const httpProtocol = url.protocol === 'wss:' ? 'https:' : 'http:'
    return `${httpProtocol}//${url.host}`
  } catch {
    return 'http://127.0.0.1:5001'
  }
}

const PrintersComponent = () => {
  const [form] = Form.useForm()
  const { sendConfig, sendPrintJob, isConnected, reconnect, connectionTarget } =
    usePrintAgent()
  const {
    printConfig,
    loading: configLoading,
    save: savePrintConfig
  } = usePrintConfig()
  const [detectedPrinters, setDetectedPrinters] = React.useState<string[]>([])
  const [detecting, setDetecting] = React.useState(false)
  const [dummyServiceType, setDummyServiceType] = React.useState('TAKEAWAY')
  const [dummyInstructions, setDummyInstructions] = React.useState(
    'No onion. Pack chutney separately.'
  )
  const [dummyPrinter, setDummyPrinter] = React.useState<string | undefined>(undefined)
  const [routingDraft, setRoutingDraft] = React.useState<Record<string, string>>({})
  const [invoicePrinterDraft, setInvoicePrinterDraft] = React.useState<string | undefined>(undefined)
  const [kotPrinterDraft, setKotPrinterDraft] = React.useState<string | undefined>(undefined)
  const [savingRouting, setSavingRouting] = React.useState(false)

  React.useEffect(() => {
    setRoutingDraft(printConfig.cuisineRouting ?? {})
    setInvoicePrinterDraft(printConfig.invoicePrinter)
    setKotPrinterDraft(printConfig.kotPrinter)
  }, [printConfig])

  const normalizeKOTServiceType = (rawServiceType: string) => {
    const normalized = (rawServiceType || '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/[-\s]/g, '_')

    if (
      normalized === 'TAKEAWAY' ||
      normalized === 'TAKE_AWAY' ||
      normalized === 'TAKEWAY'
    ) {
      return 'PARCEL'
    }
    if (normalized === 'DINEIN' || normalized === 'DINE_IN') {
      return 'DINEIN'
    }
    if (normalized === 'DINEOUT' || normalized === 'DINE_OUT') {
      return 'DINEOUT'
    }
    return ''
  }

  const handleDetectPrinters = async () => {
    setDetecting(true)
    try {
      const override = window.localStorage.getItem(OVERRIDE_KEY)
      const httpBase = toHttpBase(override || connectionTarget)
      const response = await fetch(`${httpBase}/system-printers`)
      const data = await response.json()
      const names: string[] = data.printers || []
      setDetectedPrinters(names)
      if (names.length === 0) {
        message.warning(
          'No installed printers found. Check Windows Settings > Printers & Scanners.'
        )
      } else {
        message.success(`Found ${names.length} installed printer(s).`)
      }
    } catch (error) {
      message.error(
        'Could not reach Print Agent to detect printers. Ensure it is running and connected.'
      )
    } finally {
      setDetecting(false)
    }
  }

  const handleDummyPrint = () => {
    const instructions = dummyInstructions.trim()
    if (!instructions) {
      message.error(
        'Instructions are mandatory for KOT. Please enter instructions.'
      )
      return
    }
    
    if (!dummyPrinter) {
      message.error('Please select a target printer for the Dummy KOT.')
      return
    }

    sendPrintJob({
      documentType: 'KOT',
      printerRole: dummyPrinter!,
      copies: 1,
      orderId: `DUMMY-${Date.now().toString().slice(-6)}`,
      restaurant: 'FoodTolls Demo Kitchen',
      serviceType: normalizeKOTServiceType(dummyServiceType),
      comments: instructions,
      customerName: '',
      subtotal: 0,
      tax: 0,
      discount: 0,
      total: 0,
      payMethod: '',
      items: [
        {
          qty: 2,
          name: 'Paneer Tikka Wrap',
          price: 0,
          notes: 'Less spicy'
        },
        {
          qty: 1,
          name: 'Masala Fries',
          price: 0,
          notes: 'Extra seasoning'
        },
        {
          qty: 1,
          name: 'Lime Soda',
          price: 0,
          notes: 'No ice'
        }
      ]
    })
    message.success(
      `Dummy KOT sent (Service: ${normalizeKOTServiceType(dummyServiceType) || 'EMPTY'})`
    )
  }

  React.useEffect(() => {
    try {
      const override = window.localStorage.getItem(OVERRIDE_KEY)
      const parsed = new URL(override || connectionTarget)
      form.setFieldsValue({
        agentHost: parsed.hostname,
        agentPort: Number(parsed.port || 5001),
        agentPath: parsed.pathname || '/ws'
      })
    } catch {
      form.setFieldsValue({
        agentHost: '127.0.0.1',
        agentPort: 5001,
        agentPath: '/ws'
      })
    }
  }, [connectionTarget, form])

  const applyEndpointOverride = (values: {
    agentHost: string
    agentPort: number
    agentPath: string
  }) => {
    const host = values.agentHost?.trim() || '127.0.0.1'
    const port = Number(values.agentPort) || 5001
    const path = values.agentPath?.startsWith('/')
      ? values.agentPath
      : `/${values.agentPath || 'ws'}`
    const wsUrl = `ws://${host}:${port}${path}`
    window.localStorage.setItem(OVERRIDE_KEY, wsUrl)
    reconnect()
    message.success(`Print Agent endpoint applied: ${wsUrl}`)
  }

  return (
    <div style={{ padding: '20px' }}>
      <Title level={3}>Printer Configuration</Title>
      <Text type='secondary' style={{ display: 'block', marginBottom: '20px' }}>
        Configure your WiFi, LAN, or Bluetooth thermal receipt printer manually.
        USB printers are automatically detected by the Print Agent.
      </Text>

      <Card style={{ maxWidth: '600px' }}>
        <Form
          form={form}
          layout='vertical'
          initialValues={{
            role: 'INVOICE',
            connectionType: 'spooler',
            baudRate: 0,
            agentHost: '127.0.0.1',
            agentPort: 5001,
            agentPath: '/ws'
          }}
        >
          <Title level={5} style={{ marginTop: 0 }}>
            Agent Connection
          </Title>
          <Form.Item
            name='agentHost'
            label='Agent Host'
            rules={[{ required: true, message: 'Please enter host' }]}
          >
            <Input placeholder='127.0.0.1' />
          </Form.Item>

          <Form.Item
            name='agentPort'
            label='Agent Port'
            rules={[{ required: true, message: 'Please enter port' }]}
          >
            <InputNumber
              min={1}
              max={65535}
              style={{ width: '100%' }}
              placeholder='5001'
            />
          </Form.Item>

          <Form.Item
            name='agentPath'
            label='WebSocket Path'
            rules={[{ required: true, message: 'Please enter path' }]}
          >
            <Input placeholder='/ws' />
          </Form.Item>

          <Space wrap style={{ marginBottom: 20 }}>
            <Button
              onClick={() => {
                const values = form.getFieldsValue([
                  'agentHost',
                  'agentPort',
                  'agentPath'
                ])
                applyEndpointOverride(values as any)
              }}
            >
              Apply Endpoint & Reconnect
            </Button>
            <Button
              onClick={() => {
                window.localStorage.removeItem(OVERRIDE_KEY)
                reconnect()
                message.success('Using auto discovery again')
              }}
            >
              Reset to Auto Discovery
            </Button>
          </Space>

          <Form.Item>
            <Title level={5}>Test Print</Title>
            <Space wrap style={{ marginBottom: 12 }}>
              <Select
                placeholder="Select printer to test"
                style={{ width: '100%', maxWidth: '250px' }}
                value={dummyPrinter}
                onChange={setDummyPrinter}
                options={detectedPrinters.map(p => ({ label: p, value: p }))}
              />
              <Button onClick={handleDummyPrint} disabled={!isConnected || !dummyPrinter}>
                Print Dummy KOT
              </Button>
            </Space>
            {!isConnected && (
              <Text
                type='danger'
                style={{ display: 'block', marginTop: '10px' }}
              >
                Cannot save printer config yet: Print Agent is offline. Apply
                endpoint and reconnect first.
              </Text>
            )}
          </Form.Item>
        </Form>
      </Card>

      <Card style={{ maxWidth: '700px', marginTop: 24 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <Button type="primary" loading={detecting} onClick={handleDetectPrinters}>
            Detect Installed Printers
          </Button>
          <Text type="secondary">Detect Windows printers before configuring routing.</Text>
        </Space>
      </Card>

      {/* ── Cuisine → Printer Role Routing ───────────────────────────── */}
      <Card style={{ maxWidth: '700px', marginTop: 24 }}>
        <Title level={5} style={{ marginTop: 0 }}>
          Cuisine Routing
        </Title>
        <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
          Map each cuisine type to the Windows printer that should receive its KOT.
          Leave blank to use the <b>default</b> printer. Items whose cuisine is not
          listed here will use the default printer.
        </Text>

        {detectedPrinters.length === 0 && (
          <Text type='warning' style={{ display: 'block', marginBottom: 16 }}>
            Detect installed printers first (using the <b>Detect Printers</b>{' '}
            button above) before configuring cuisine routing.
          </Text>
        )}

        <Table
              size='small'
              pagination={false}
              loading={configLoading}
              rowKey='cuisine'
              dataSource={[
                ...CUISINE_TYPES.map((c) => ({ cuisine: c })),
                { cuisine: 'default' }
              ]}
              columns={[
                {
                  title: 'Cuisine',
                  dataIndex: 'cuisine',
                  render: (c: string) =>
                    c === 'default' ? (
                      <b>default (fallback for all unmatched)</b>
                    ) : (
                      c
                    )
                },
                {
                  title: 'Printer',
                  dataIndex: 'cuisine',
                  render: (c: string) => (
                    <Select
                      allowClear
                      showSearch
                      placeholder={c === 'default' ? 'KITCHEN (fallback)' : 'Select printer'}
                      style={{ width: '100%', maxWidth: '220px' }}
                      value={routingDraft[c] ?? undefined}
                      filterOption={(input, option) =>
                        (option?.label as string ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={detectedPrinters.map((p) => ({
                        label: p,
                        value: p
                      }))}
                      onChange={(val) =>
                        setRoutingDraft((prev) => {
                          if (!val) {
                            const next = { ...prev }
                            delete next[c]
                            return next
                          }
                          return { ...prev, [c]: val }
                        })
                      }
                    />
                  )
                }
              ]}
            />

            <Title level={5} style={{ marginTop: 32 }}>
              Invoice / Bill Printer
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Select the printer that should receive full bill printouts.
            </Text>
            <Select
              allowClear
              showSearch
              placeholder="Select invoice printer"
              style={{ width: '100%', maxWidth: '300px', marginBottom: 24 }}
              value={invoicePrinterDraft}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={detectedPrinters.map(p => ({ label: p, value: p }))}
              onChange={setInvoicePrinterDraft}
            />

            <Title level={5} style={{ marginTop: 16 }}>
              Master KOT Printer
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Select the printer that should receive a master KOT containing ALL items in the order.
              Leave blank to disable Master KOT printing.
            </Text>
            <Select
              allowClear
              showSearch
              placeholder="Select master KOT printer"
              style={{ width: '100%', maxWidth: '300px' }}
              value={kotPrinterDraft}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={detectedPrinters.map(p => ({ label: p, value: p }))}
              onChange={setKotPrinterDraft}
            />

            <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <Button
                type='primary'
                loading={savingRouting}
                onClick={async () => {
                  setSavingRouting(true)
                  try {
                    await savePrintConfig(routingDraft, invoicePrinterDraft, kotPrinterDraft)
                    message.success('Printer routing configuration saved.')
                  } catch {
                    message.error('Failed to save printer configuration.')
                  } finally {
                    setSavingRouting(false)
                  }
                }}
              >
                Save Printer Configuration
              </Button>
            </div>
      </Card>
    </div>
  )
}

export default PrintersComponent
